"use client"

import { Button } from "@/components/ui/button"
import { Pencil, Wand2, Calculator, FileText, BookA, Book, Eye, Lightbulb, Highlighter, Share2 } from "lucide-react"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { QuestionResponse } from "@/services/practice"
import { AssistTool } from "@/components/dashboard/practice/session/header"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { MetadataEditorModal } from "@/components/dashboard/practice/session/metadata-editor-modal"
import { useState } from "react"

interface MobileToolbarProps {
    question?: QuestionResponse | null;
    userRole?: number;
    activeTool?: AssistTool | null;
    onToolChange?: (tool: AssistTool | null) => void;
    title?: string;
    onShowSolution?: () => void;
    showSolutionButton?: boolean;
    onQuestionUpdate?: (updatedQuestion: any) => void;
    isMath?: boolean;
    isCalculatorOpen?: boolean;
    onToggleCalculator?: () => void;
    onToggleReference?: () => void;
    isHighlightActive?: boolean;
    onToggleHighlight?: () => void;
    onShare?: () => void;
}

export function MobileToolbar({
    question,
    userRole = 1,
    activeTool,
    onToolChange,
    title = "",
    onShowSolution,
    showSolutionButton,
    onQuestionUpdate,
    isMath,
    isCalculatorOpen,
    onToggleCalculator,
    onToggleReference,
    isHighlightActive = false,
    onToggleHighlight,
    onShare
}: MobileToolbarProps) {

    const showReference = (
        title.toLowerCase().includes("math") ||
        title.toLowerCase().includes("algebra") ||
        title.toLowerCase().includes("geometry") ||
        title.toLowerCase().includes("calculus") ||
        title.toLowerCase().includes("trigonometry") ||
        title.toLowerCase().includes("problem solving") ||
        title.toLowerCase().includes("data analysis")
    )

    return (
        <div className="flex md:hidden items-center justify-between bg-background border-b px-4 py-2 gap-2 overflow-x-auto">
            {/* Solution Button - Visible when allowed */}
            {showSolutionButton && onShowSolution && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onShowSolution}
                    className="text-base gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20 animate-in fade-in"
                >
                    <Lightbulb className="h-4 w-4" />
                    <span className="text-base font-medium">Solution</span>
                </Button>
            )}

            {/* Tools (Always visible) */}
            <div className="flex items-center gap-1">
                {isMath && (
                    <Button
                        variant={isCalculatorOpen ? "secondary" : "ghost"}
                        size="sm"
                        onClick={() => onToggleCalculator?.()}
                        className="h-9 px-3 text-muted-foreground flex items-center gap-2"
                    >
                        <Calculator className="h-4 w-4" />
                        <span className="font-medium text-sm">Calculator</span>
                    </Button>
                )}

                {!isMath && (
                    <>
                        <Button
                            variant={activeTool === 'dictionary' ? "secondary" : "ghost"}
                            size="icon"
                            onClick={() => onToolChange?.(activeTool === 'dictionary' ? null : 'dictionary')}
                            className="h-9 w-9 text-muted-foreground"
                        >
                            <Book className="h-4 w-4" />
                        </Button>

                        <Button
                            variant={activeTool === 'vocab' ? "secondary" : "ghost"}
                            size="icon"
                            onClick={() => onToolChange?.(activeTool === 'vocab' ? null : 'vocab')}
                            className="h-9 w-9 text-muted-foreground"
                        >
                            <BookA className="h-4 w-4" />
                        </Button>

                        <Button
                            variant={isHighlightActive ? "secondary" : "ghost"}
                            size="icon"
                            onClick={onToggleHighlight}
                            className={cn(
                                "h-9 w-9",
                                isHighlightActive
                                    ? "text-yellow-600 bg-yellow-100 dark:bg-yellow-500/20 dark:text-yellow-300"
                                    : "text-muted-foreground"
                            )}
                            title="Highlighter"
                        >
                            <Highlighter className="h-4 w-4" />
                        </Button>
                    </>
                )}

                {onShare && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onShare}
                        className="h-9 w-9 text-muted-foreground"
                        title="Share as image"
                    >
                        <Share2 className="h-4 w-4" />
                    </Button>
                )}
            </div>

            {/* Reference (Conditional) */}
            {showReference && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleReference?.()}
                    className="h-9 px-3 text-muted-foreground flex items-center gap-2"
                >
                    <FileText className="h-4 w-4" />
                    <span className="font-medium text-sm">Reference</span>
                </Button>
            )}
        </div>
    )
}
